import {
  Dispatch,
  Fragment,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  CollaboratorCalibration,
  InternalGradeStat,
  MatrixDevelopmentItem,
} from "../../types/api/potentialMatrix.type";
import {
  CollaboratorInfo,
  FontWeightEnum,
  IconSizeEnum,
} from "../../types/AppTypes";
import {
  CONSULTANT_LABEL,
  EMPTY_STRING,
  JUNIOR_CONSULTANT_LABEL,
  MANAGER_LABEL,
  SENIOR_CONSULTANT_LABEL,
  SENIOR_MANAGER_LABEL,
  TOTAL_LABEL,
} from "../../constants/Dictionary";
import {
  StyledBottomBar,
  StyledCell,
  StyledCellContainer,
  StyledCellContentCollaborators,
  StyledCellContentDownload,
  StyledIconContainer,
  StyledMatrixButton,
  StyledQuadrantAxisLabels,
  StyledMatrix,
} from "./MatrixGrid.styled";
import {
  COLLABORATORS_LIMIT,
  MatrixSizes,
  MatrixViewModeV3,
} from "../../constants/PotentialMatrixConstants";
import { Chip, ChipSizeEnum } from "../core/Chip/Chip";
import { CollaboratorChip } from "../core/CollaboratorChip/CollaboratorChip";
import { DownloadCollaboratorChip } from "../core/DownloadCollaboratorChip/DownloadCollaboratorChip";
import { IconManager } from "../core/IconManager/IconManager";
import { Loader } from "../core/Loader/Loader";
import { BodyTyp, CaptionsTyp, TitlesTyp } from "../Typography/Typography";
import { theme } from "../../constants/StyledComponents";
import { ReactComponent as DetailsIcon } from "../../icons/details_search.svg";
import { ReactComponent as CheckIcon } from "../../icons/check.svg";
import { ReactComponent as RocketIcon } from "../../icons/rocket.svg";
import { ReactComponent as FlagIcon } from "../../icons/flag.svg";
import { ScreenEnum } from "../../types/api/collaborators.type";
import { PendingCalibration } from "../CalibrationModal/CalibrationModal";
import {
  INTERNAL_GRADE_LABEL_TO_KEY,
  INTERNAL_GRADE_META,
  QUADRANT_LABELS,
} from "../../constants/AppConstants";
import { BadgeChip, BadgeVariant } from "../BadgeChip/BadgeChip";

export type MatrixCalibrationProps = {
  collaborator: CollaboratorCalibration;
  onChange: Dispatch<SetStateAction<PendingCalibration | null>>;
  pendingCalibration: PendingCalibration | null;
};

type RowValue = {
  value: string;
  label: string;
  color: string;
};

type MatrixProps = {
  data: MatrixDevelopmentItem[];
  internalGradeStats?: InternalGradeStat[];
  size: MatrixSizes;
  loading?: boolean;
  viewMode?: MatrixViewModeV3;
  onClickResume?: (collaborator: CollaboratorInfo) => void;
  selectedItem?: MatrixDevelopmentItem;
  onClickItem?: (item: MatrixDevelopmentItem) => void;
  matrixCalibration?: MatrixCalibrationProps;
  showDetailsChip?: boolean;
  isAnimatingOpen?: boolean;
  showDownloadPreview?: boolean;
};

export const MatrixGrid = ({
  data,
  internalGradeStats = [],
  selectedItem,
  size,
  loading = false,
  viewMode,
  onClickItem,
  onClickResume,
  matrixCalibration,
  showDetailsChip = true,
  isAnimatingOpen,
  showDownloadPreview = false,
}: MatrixProps) => {
  const divRef = useRef<HTMLDivElement>(null);

  const rowValues: RowValue[] = [
    {
      value: "5 - Senior Manager",
      label: SENIOR_MANAGER_LABEL,
      color: "#6395BC",
    },
    {
      value: "4 - Manager",
      label: MANAGER_LABEL,
      color: "#6FAFA7",
    },
    {
      value: "3 - Senior Consultant",
      label: SENIOR_CONSULTANT_LABEL,
      color: "#B58FB3",
    },
    {
      value: "2 - Consultant",
      label: CONSULTANT_LABEL,
      color: "#E38EA8",
    },
    {
      value: "1 - Junior Consultant",
      label: JUNIOR_CONSULTANT_LABEL,
      color: "#DDB378",
    },
  ];

  const COLUMN_OPACITIES = ["33", "4D", "66", "99"];

  const REVERSED_QUADRANTS = Object.values(ScreenEnum).slice().reverse();

  const calcMatrix = () => {
    const matrix = [] as [string, string, string][][];
    rowValues.forEach((rv) => {
      const row = [] as [string, string, string][];
      REVERSED_QUADRANTS.forEach((screen, colIndex) => {
        const cell: [string, string, string] = [
          rv.value,
          screen,
          `${rv.color}${COLUMN_OPACITIES[colIndex]}`,
        ];
        row.push(cell);
      });
      matrix.push(row);
    });
    return matrix;
  };

  const matrix = calcMatrix();

  const renderCollaboratorsCellContent = (
    matrixItem: MatrixDevelopmentItem,
    showDetails: boolean,
  ) => {
    const COLLABORATOR_SLOTS = COLLABORATORS_LIMIT.DEVELOPMENT;

    const paddedCollaborators = Array.from(
      { length: COLLABORATOR_SLOTS },
      (_, i) => matrixItem.collaborators[i] ?? null,
    );

    return (
      <StyledCellContentCollaborators>
        <div className="cell-content-collaborators-header">
          <div className={"cell-content-collaborators-container"}>
            {paddedCollaborators.map((item, index) => {
              const gradeKey = item?.internalGrade
                ? INTERNAL_GRADE_LABEL_TO_KEY[item.internalGrade]
                : undefined;
              const gradeMeta = gradeKey
                ? INTERNAL_GRADE_META[gradeKey]
                : undefined;
              return (
                <div
                  key={item?.id ?? `placeholder-${index}`}
                  className={`collaborator-chip-slot ${item == null ? "invisible" : ""}`}
                >
                  {item != null ? (
                    <CollaboratorChip
                      collaborator={item}
                      {...(item.internalGrade && {
                        borderColor: gradeMeta?.color,
                      })}
                      onClick={onClickResume}
                      size={ChipSizeEnum.SMALL}
                    />
                  ) : (
                    <div style={{ height: 18 }} />
                  )}
                </div>
              );
            })}
          </div>
          {showDetails && (
            <Chip
              option={{
                value: EMPTY_STRING,
                label: (
                  <StyledMatrixButton>
                    <div className="details-icon-wrapper">
                      <IconManager
                        size={IconSizeEnum.SMALL}
                        color={theme.colors.black}
                      >
                        <DetailsIcon />
                      </IconManager>
                    </div>
                    {matrixItem.count > 0 && (
                      <BodyTyp fontWeight={FontWeightEnum.SEMIBOLD} variant="3">
                        {`${TOTAL_LABEL} ${matrixItem.count}`}
                      </BodyTyp>
                    )}
                  </StyledMatrixButton>
                ),
              }}
              size={ChipSizeEnum.SMALL}
              hoverEffect
              {...(matrixItem &&
                onClickItem && {
                  onSelectChip: () => onClickItem(matrixItem),
                })}
              selectedChip
            />
          )}
        </div>
      </StyledCellContentCollaborators>
    );
  };

  const renderDownloadCellContent = (matrixItem?: MatrixDevelopmentItem) => {
    return (
      <StyledCellContentDownload>
        {matrixItem && (
          <div className="cell-content-collaborators-header">
            <div className="cell-content-collaborators-container">
              {matrixItem.collaborators.map((item) => {
                const gradeKey = item.internalGrade
                  ? INTERNAL_GRADE_LABEL_TO_KEY[item.internalGrade]
                  : undefined;
                const gradeMeta = gradeKey
                  ? INTERNAL_GRADE_META[gradeKey]
                  : undefined;
                return (
                  <DownloadCollaboratorChip
                    key={item.id}
                    collaborator={item}
                    {...(item.internalGrade && {
                      borderColor: gradeMeta?.color,
                    })}
                  />
                );
              })}
            </div>
          </div>
        )}
      </StyledCellContentDownload>
    );
  };

  const renderCellContent = (
    item: MatrixDevelopmentItem,
    row: string,
    col: string,
    count: number,
  ) => {
    if (!isBig) {
      if (
        selectedItem &&
        selectedItem.internalGrade === row &&
        selectedItem.screen === col
      ) {
        return (
          <div className="count-container">
            <IconManager size={IconSizeEnum.SMALL} color={theme.colors.black}>
              <CheckIcon />
            </IconManager>
          </div>
        );
      }
      return null;
    }
    if (showDownloadPreview) {
      return renderDownloadCellContent(item);
    }

    if (viewMode === MatrixViewModeV3.COLLABORATOR) {
      if (count === 0) {
        return null;
      }

      return renderCollaboratorsCellContent(item, showDetailsChip);
    }

    return null;
  };

  const renderEmptyCellContent = () => {
    if (showDownloadPreview) {
      return renderDownloadCellContent();
    }

    return null;
  };

  const getItemData = (row: string, col: string) => {
    return data.find(
      (d) => d.screen === col && d.internalGrade === row && d.count > 0,
    );
  };

  const calibrationGrade =
    matrixCalibration?.collaborator.collaborator.internalGrade ?? "";

  const proposedScreen =
    matrixCalibration?.collaborator.calibration?.proposedScreen;

  const finalScreen =
    matrixCalibration?.pendingCalibration?.finalScreen ??
    matrixCalibration?.collaborator.calibration?.finalScreen;

  const startingCellId =
    matrixCalibration && proposedScreen
      ? `${calibrationGrade}_${proposedScreen}}`
      : "";

  const currentCellId =
    matrixCalibration && finalScreen
      ? `${calibrationGrade}_${finalScreen}}`
      : "";

  const isSameCalibrationCell =
    !!startingCellId && startingCellId === currentCellId;

  const proposedPercentage =
    matrixCalibration?.collaborator.calibration?.proposedDimensionPercentage ??
    0;

  const finalPercentage =
    matrixCalibration?.pendingCalibration?.finalDimensionPercentage ??
    matrixCalibration?.collaborator.calibration?.finalDimensionPercentage ??
    0;

  const getRowTotal = (internalGrade: string): number =>
    internalGradeStats.find((stat) => stat.internalGrade === internalGrade)
      ?.count ?? 0;

  const isBig = size === MatrixSizes.BIG;

  const rowCellRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [rowHeights, setRowHeights] = useState<number[]>([]);

  useEffect(() => {
    if (!showDownloadPreview) return;

    const heights = rowCellRefs.current.map(
      (el) => el?.getBoundingClientRect().height ?? 0,
    );
    setRowHeights(heights);
  }, [viewMode, data, showDownloadPreview]);

  return (
    <StyledMatrix $size={size} $isAnimatingOpen={isAnimatingOpen}>
      <div className="matrix-content">
        <div className="row-title">
          {rowValues.map((l, index) =>
            isBig ? (
              <Fragment key={l.value}>
                <div
                  key={l.value}
                  className="container-internal"
                  style={
                    showDownloadPreview && rowHeights[index]
                      ? { height: rowHeights[index], justifyContent: "center" }
                      : undefined
                  }
                >
                  <BodyTyp fontWeight={FontWeightEnum.SEMIBOLD} variant="1">
                    {l.label}
                  </BodyTyp>
                  <BadgeChip colorVariant={BadgeVariant.PRIMARY}>
                    {getRowTotal(l.value)}
                  </BadgeChip>
                </div>
              </Fragment>
            ) : (
              <CaptionsTyp key={l.value} fontWeight={FontWeightEnum.SEMIBOLD}>
                {l.label}
              </CaptionsTyp>
            ),
          )}
        </div>
        <Loader
          loading={loading}
          size={20}
          className="loader-matrix-container"
          contentClassName="loader-matrix-content"
        >
          <div className="matrix-grid" ref={divRef}>
            {matrix.map((row, rowIndex) =>
              row.map((cell, colIndex) => {
                const [row, col, color] = cell;
                const item = getItemData(row as string, col as string);
                const cellId = `${row}_${col}}`;
                const isNotGlobal =
                  item &&
                  item.count > 0 &&
                  (showDownloadPreview || viewMode !== MatrixViewModeV3.GLOBAL);
                const isProposedCell = cellId === startingCellId;
                const isFinalCell = cellId === currentCellId;

                return (
                  <StyledCell
                    key={cellId}
                    ref={
                      colIndex === 0
                        ? (el) => (rowCellRefs.current[rowIndex] = el)
                        : undefined
                    }
                    $bgColor={color as string}
                    $size={size}
                    $showDownloadPreview={showDownloadPreview}
                  >
                    <StyledCellContainer
                      {...(item &&
                        !showDownloadPreview &&
                        viewMode === MatrixViewModeV3.GLOBAL &&
                        onClickItem && {
                          $clickable: true,
                        })}
                    >
                      <div
                        className={
                          isBig
                            ? `cell-main-box ${isNotGlobal ? "active" : ""}`
                            : "cell-main-box-small"
                        }
                        {...(item &&
                          !showDownloadPreview &&
                          viewMode === MatrixViewModeV3.GLOBAL &&
                          onClickItem && {
                            onClick: () => onClickItem(item),
                          })}
                      >
                        {data.map((d) => {
                          const [row, col] = cell;
                          if (d.screen === col && d.internalGrade === row) {
                            if (item) {
                              return (
                                <Fragment
                                  key={`${d.internalGrade}-${d.screen}`}
                                >
                                  {renderCellContent(
                                    item,
                                    row as string,
                                    col as string,
                                    d.count,
                                  )}
                                </Fragment>
                              );
                            } else {
                              return (
                                <Fragment
                                  key={`${d.internalGrade}-${d.screen}`}
                                >
                                  {renderEmptyCellContent()}
                                </Fragment>
                              );
                            }
                          }
                          return null;
                        })}
                      </div>
                      {matrixCalibration !== undefined &&
                        (isProposedCell || isFinalCell) && (
                          <StyledBottomBar $isActive>
                            <StyledIconContainer
                              $isDark={false}
                              $isVisible={
                                isProposedCell && !isSameCalibrationCell
                              }
                            >
                              <IconManager size={IconSizeEnum.XSMALL}>
                                <RocketIcon />
                              </IconManager>
                            </StyledIconContainer>
                            <div className="draggable-collaborator-chip">
                              <div className="chip-container">
                                <CollaboratorChip
                                  collaborator={
                                    matrixCalibration.collaborator.collaborator
                                  }
                                  backgroundColor={
                                    isFinalCell
                                      ? theme.colors.green_3
                                      : theme.colors.yellow_3
                                  }
                                  size={ChipSizeEnum.SMALL}
                                  calibration={{
                                    percentage: isFinalCell
                                      ? finalPercentage
                                      : proposedPercentage,
                                  }}
                                />
                              </div>
                            </div>
                            <StyledIconContainer
                              $isDark
                              $isVisible={isFinalCell}
                            >
                              <IconManager
                                color={theme.colors.white}
                                size={IconSizeEnum.XSMALL}
                              >
                                <FlagIcon />
                              </IconManager>
                            </StyledIconContainer>
                          </StyledBottomBar>
                        )}
                    </StyledCellContainer>
                  </StyledCell>
                );
              }),
            )}
          </div>
        </Loader>
      </div>
      <StyledQuadrantAxisLabels
        $size={size}
        $numCol={Object.keys(ScreenEnum).length}
      >
        {Object.values(ScreenEnum)
          .slice()
          .reverse()
          .map((key) => (
            <div key={key} className="axis-cell">
              <TitlesTyp
                fontWeight={FontWeightEnum.SEMIBOLD}
                variant={isBig ? "5" : "6"}
              >
                {key}
              </TitlesTyp>
              {isBig && (
                <BodyTyp fontWeight={FontWeightEnum.MEDIUM} variant="2">
                  {QUADRANT_LABELS[key]}
                </BodyTyp>
              )}
            </div>
          ))}
      </StyledQuadrantAxisLabels>
    </StyledMatrix>
  );
};
